import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import {
  ImageCoverView,
  ImageScaleView,
  ImageScaleViewOpts,
  View,
} from '@play-co/timestep-core/ui';
import { ProgressBar } from 'src/game/components/shared/ProgressBar';
import {
  LangBitmapFontTextShadowView,
  LangBitmapFontTextShadowViewOpts,
} from 'src/lib/ui/components/LangBitmapFontTextShadowView';

const assets = {
  framePurple: 'assets/ui/match2/common/bar_frame_purple.png',
  fillGray: 'assets/ui/match2/common/bar_fill_grey.png',
  fillGreen: 'assets/ui/match2/common/bar_fill_green.png',
  fillPurple: 'assets/ui/match2/common/bar_fill_purple.png',
};

type Match2LiveOpsProgressbarOpts = {
  superview?: View;
  width?: number;
  height?: number;
  barWidth?: number;
  barHeight?: number;
  labelOpts?: LangBitmapFontTextShadowViewOpts;
  title?: string;
  currentProgress: number;
  maxProgress: number;
  accumulatedProgress?: number;
  earnedProgress?: number;
  minWidth?: number;
};

export default class Match2LiveOpsProgressbar extends FlexBoxView {
  public greenProgressBar: ProgressBar;
  public greyProgressBar: ProgressBar;
  public purpleProgressBar: ProgressBar;
  private icon: ImageScaleView | ImageCoverView;

  constructor(args: Match2LiveOpsProgressbarOpts) {
    const {
      superview,
      width = 540,
      height = 120,
      barWidth = 400,
      barHeight = 51,
      labelOpts,
      title,
      currentProgress,
      accumulatedProgress,
      maxProgress,
      earnedProgress,
      minWidth,
    } = args;

    super({
      superview,
      width,
      height,
      flex: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    if (title) {
      new LangBitmapFontTextShadowView({
        superview: this,
        localeText: () => title,
        color: '#985C25',
        font: 'Body',
        size: 24,
        text: 'test',
        align: 'center',
        verticalAlign: 'center',
        width: 540,
        height: 40,
        textShadowOffsetX: 0,
        textShadowOffsetY: 4,
        textShadowColor: '#FFFFFF',
      });
    }

    const progressBarContainer = new View({
      superview: this,
      width: barWidth,
      height: barHeight,
    });

    this.greyProgressBar = new ProgressBar({
      superview: progressBarContainer,
      bgStyle: {
        image: assets.framePurple,
        width: barWidth,
        height: barHeight,
      },
      fillStyle: {
        image: assets.fillGray,
        width: barWidth - 5,
        height: barHeight,
        x: 0,
        y: 0,
        sourceSlices: {
          horizontal: { left: 20, right: 20 },
          vertical: { top: 20, bottom: 20 },
        },
        opacity: 0.8,
        minWidth,
      },
      domain: [0, maxProgress],
      minWidth,
    });

    this.greenProgressBar = new ProgressBar({
      superview: progressBarContainer,
      bgStyle: {
        image: '',
        width: barWidth,
        height: barHeight,
      },
      fillStyle: {
        image: assets.fillGreen,
        width: barWidth - 5,
        height: barHeight - 1,
        x: 5,
        y: -1,
        sourceSlices: {
          horizontal: { left: 30, right: 30 },
          vertical: { top: 30, bottom: 30 },
        },
      },
      domain: [0, maxProgress],
      minWidth,
    });

    this.purpleProgressBar = new ProgressBar({
      superview: progressBarContainer,
      bgStyle: {
        image: '',
        width: barWidth,
        height: barHeight,
      },
      fillStyle: {
        image: assets.fillPurple,
        width: barWidth - 5,
        height: barHeight - 7,
        x: 5,
        y: 2,
        sourceSlices: {
          horizontal: { left: 30, right: 30 },
          vertical: { top: 30, bottom: 30 },
        },
      },
      domain: [0, maxProgress],
      minWidth,
    });

    const label = new LangBitmapFontTextShadowView({
      superview: progressBarContainer,
      localeText: () => '?/?',
      color: '#FFFFFF',
      font: 'Body',
      size: 36,
      x: this.greenProgressBar.style.width * 0.5,
      y: this.greenProgressBar.style.height * 0.5 - 4,
      align: 'center',
      verticalAlign: 'center',
      width: this.greenProgressBar.style.width,
      height: this.greenProgressBar.style.height,
      centerOnOrigin: true,
      textShadowOffsetX: 0,
      textShadowOffsetY: 4,
      ...labelOpts,
    });

    const updateProgress = (value: number) => {
      value = Math.round(Math.min(value, this.greenProgressBar.domain[1]));
      label.updateOpts({
        localeText: () => `${value}/${this.greenProgressBar.domain[1]}`,
      });
    };
    this.greenProgressBar.on('progress', updateProgress);
    updateProgress(0);

    this.greenProgressBar.progress = currentProgress;
    this.greyProgressBar.progress = currentProgress;
    this.purpleProgressBar.progress = accumulatedProgress ?? 0;

    this.icon = new ImageScaleView({
      superview: progressBarContainer,
      width: 80,
      height: 80,
      centerOnOrigin: true,
      x: 15,
      y: this.greenProgressBar.style.height * 0.5 - 7,
      scaleMethod: 'contain',
    });

    if (earnedProgress) {
      new LangBitmapFontTextShadowView({
        superview: progressBarContainer,
        localeText: () => `+${earnedProgress}`,
        color: '#FFFF5B',
        font: 'Body',
        size: 36,
        x: this.greenProgressBar.style.width - 90,
        y: -3,
        align: 'center',
        verticalAlign: 'center',
        width: 76,
        height: this.greenProgressBar.style.height,
        textShadowOffsetX: 0,
        textShadowOffsetY: 4,
      });
    }
  }

  public updateIcon(
    image: ImageScaleViewOpts | ImageScaleView | ImageCoverView,
  ) {
    if (image instanceof ImageScaleView || image instanceof ImageCoverView) {
      const previousIcon = this.icon;
      this.icon = image;
      this.icon.updateOpts({
        superview: previousIcon.getSuperview()!,
        x: previousIcon.style.x,
        y: previousIcon.style.y,
      });
      previousIcon.removeFromSuperview();
      return;
    }

    this.icon.updateOpts({ ...image });
  }

  public animateProgress(newProgress: number) {
    return this.greenProgressBar.animateProgress(newProgress, 2000);
  }

  public setProgress(progress: number) {
    this.greenProgressBar.progress = progress;
    this.greyProgressBar.progress = progress;
  }
}
